import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, Mail, Check, Pencil } from "lucide-react";
import { DriverShell } from "@/components/shells";
import { Panel, Spinner, Btn, Input } from "@/components/kit";
import { useAsync } from "@/lib/hooks";
import { loadDriverContext } from "@/lib/driverData";
import { api } from "@/lib/api";
import { formatReg, fmtDate } from "@/lib/vda";

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value || "—"}</span>
    </div>
  );
}

function DriverDetails({ driver }) {
  const [email, setEmail] = useState(driver?.email || "");
  const [editing, setEditing] = useState(!driver?.email);
  const [saved, setSaved] = useState(driver?.email || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.driverDetails(email.trim());
      setSaved(res.email);
      setEditing(false);
      toast.success("Your email has been saved.");
    } catch (err) {
      toast.error(err.message || "Could not save your email.");
    } finally { setSaving(false); }
  };

  return (
    <Panel className="p-5 mt-4" data-testid="driver-details">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-muted-foreground" />
        <h2 className="font-semibold">Driver details</h2>
      </div>
      <p className="text-sm text-muted-foreground mt-1">
        Your name and mobile come from your fleet record. Add an email so we can send booking confirmations.
      </p>
      <div className="mt-4 space-y-3">
        <Row label="Name" value={driver?.full_name} />
        <Row label="Mobile" value={driver?.mobile_number} />
        <div className="pt-1">
          <span className="text-sm text-muted-foreground">Email</span>
          {editing ? (
            <div className="mt-2 space-y-2">
              <Input data-testid="driver-email-input" type="email" inputMode="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
              <div className="flex gap-2">
                <Btn data-testid="driver-email-save" onClick={save} loading={saving} className="flex-1">
                  <Check className="h-4 w-4" /> Save email
                </Btn>
                {saved && (
                  <Btn variant="outline" data-testid="driver-email-cancel" onClick={() => { setEmail(saved); setEditing(false); }}>
                    Cancel
                  </Btn>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-1 flex items-center justify-between">
              <span className="text-sm font-medium" data-testid="driver-email-value">{saved}</span>
              <button data-testid="driver-email-edit" onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
            </div>
          )}
        </div>
      </div>
    </Panel>
  );
}

export default function DriverVehicle() {
  const nav = useNavigate();
  const { data, loading } = useAsync(() => loadDriverContext(), []);
  if (loading) return <DriverShell back="/driver"><Spinner /></DriverShell>;
  const { vehicle: v, allocation, customer, driver } = data;
  return (
    <DriverShell back="/driver" title="Your vehicle">
      <Panel className="p-5">
        <div className="vda-reg text-3xl font-semibold tracking-wide text-primary">{formatReg(v?.registration)}</div>
        <div className="text-foreground/80 mt-1">{v?.make} {v?.model} {v?.derivative}</div>
        <div className="mt-4">
          <Row label="MOT due" value={fmtDate(v?.mot_due_date)} />
          <Row label="Service due" value={fmtDate(v?.service_due_date)} />
          <Row label="Current mileage" value={v?.current_mileage ? v.current_mileage.toLocaleString() : "—"} />
          <Row label="Fuel type" value={v?.fuel_type} />
          <Row label="Allocation start" value={fmtDate(allocation?.start_at)} />
          <Row label="Customer" value={customer?.name} />
          <Row label="Vehicle status" value={v?.vehicle_status} />
        </div>
      </Panel>

      <DriverDetails driver={driver} />

      <Btn variant="outline" data-testid="report-allocation-btn" onClick={() => nav("/driver/allocation-issue")} className="w-full mt-4">
        <AlertCircle className="h-4 w-4" /> Report incorrect allocation
      </Btn>
    </DriverShell>
  );
}
